import React, { useEffect, useState } from "react";
import { useRefresh } from "react-admin";
import useStyles from "./style";
import classnames from "classnames";
import {
  Dropdown,
  DatePicker,
  Button,
} from "@my-scoot/component-library-legacy";
import { handleInputDateFormat } from "utils/Utils";
import { useNotifications } from "utils/hooks";
import GetAppIcon from "@material-ui/icons/GetApp";
import dataProvider from "./../../../../../data/dataProvider";
import useDesktopMediaQuery from "hooks/useDesktopMediaQuery";
import { datesValidation, getProcessedValues } from "../helpers";
import { is_empty } from "utils/validations";
import { REPORT_VALUES, report_sections } from "../../constants";
import { api } from "data";
import UpgradeTooltip from "features/Account/modules/UpgradeTooltip/UpgradeTooltip";
import { LockButton } from "common/Components/LockButton/LockButton";
import { hasMenuPermission } from "utils/AuthUtil";
import { RESOURCE_KEYS } from "utils/OrgPermissions";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import { useSebiReportsVisibility } from "features/UnifiedCommunications/apis/reports.api";

const GenerateReport = (props) => {
  const { title } = props;
  const [selectedSection, setSelectedSection] = useState(
    REPORT_VALUES.USER_PAYMENT
  );
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const showDateFilters = report_sections[selectedSection].hasDateFilters;

  const refresh = useRefresh();
  const isDesktop = useDesktopMediaQuery();
  const css = useStyles(isDesktop);

  const { pushNotification } = useNotifications();
  const dateFormat = handleInputDateFormat();
  const tildeDesktop = isDesktop && <span>~</span>;

  const handleClick = async () => {
    if (showDateFilters) {
      try {
        // Validate Entered Dates
        if (is_empty(fromDate) || is_empty(toDate)) {
          const bothEmptyText = "Start Date and End Date cannot be empty";
          const startEmptyText = "Start Date cannot be empty";
          const endEmptyText = "End Date cannot be empty";

          let errorText = "";

          if (is_empty(fromDate) && is_empty(toDate)) errorText = bothEmptyText;
          if (is_empty(fromDate) && !is_empty(toDate))
            errorText = startEmptyText;
          if (!is_empty(fromDate) && is_empty(toDate)) errorText = endEmptyText;

          return pushNotification(errorText, {
            variant: "outlined",
            color: "coral_red",
          });
        }

        datesValidation(fromDate, toDate);
      } catch (err) {
        console.error(err);
        return pushNotification("End date can not be less than start date", {
          variant: "outlined",
          color: "coral_red",
        });
      } // catch block ends
    } // traffic filter if ends

    const query = getProcessedValues(selectedSection, fromDate, toDate);
    try {
      const api_status = await dataProvider.custom_request(
        api.upload_report,
        "POST",
        query
      );
      if (api_status.status === 200) refresh();
    } catch (err) {
      console.error("records handleClick", err);
    }
  };

  const hasReportsAccess = hasMenuPermission(RESOURCE_KEYS.SECTIONS.REPORTS);
  const { data: sebiVisibility, isError: sebiVisibilityError } =
    useSebiReportsVisibility();
  const showSebiReports = Boolean(sebiVisibility?.is_visible);
  // Backend not deployed / request failed: keep the group visible but dimmed
  // instead of silently removing it, so the state is obvious.
  const sebiUnavailable = sebiVisibilityError;
  const canUseSebiReports = showSebiReports && !sebiUnavailable;

  useEffect(() => {
    if (report_sections[selectedSection]?.sebi && !canUseSebiReports) {
      setSelectedSection(REPORT_VALUES.USER_PAYMENT);
    }
  }, [canUseSebiReports, selectedSection]);
  const groupedOptions = [
    {
      name: "Business",
      suboptions: report_sections
        .filter((section) => !section.sebi)
        .map((section) => ({
          id: section.id,
          value: section.value,
          label: section.label,
        })),
    },
    ...(showSebiReports || sebiUnavailable
      ? [
          {
            name: sebiUnavailable
              ? "SEBI compliance (unavailable)"
              : "SEBI compliance",
            suboptions: report_sections
              .filter((section) => section.sebi)
              .map((section) => ({
                id: section.id,
                value: section.value,
                label: sebiUnavailable
                  ? `${section.label} (unavailable)`
                  : section.label,
                disabled: sebiUnavailable,
              })),
          },
        ]
      : []),
  ];

  return (
    <div className={css.container}>
      <Dropdown
        grouped
        groupedOptions={groupedOptions}
        value={selectedSection}
        handleChange={setSelectedSection}
        placeholder="Search By"
        size="medium"
        helperText={
          sebiUnavailable
            ? "SEBI compliance reports are unavailable right now."
            : undefined
        }
        dropdownLabel={{
          label: "Report Type",
          size: "small",
          weight: "semi-bold",
        }}
        fullWidth={!isDesktop}
        minWidth="235px"
      />
      {showDateFilters && (
        <>
          {tildeDesktop}
          <div className={classnames(css.dateWrap, css.startDateWrap)}>
            <div className={css.dataLabel}>
              Start Date<sup>*</sup>
            </div>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              placeholder={dateFormat}
              dateFormat={dateFormat}
            />
          </div>
          {tildeDesktop}
          <div className={classnames(css.dateWrap, css.endDateWrap)}>
            <div className={css.dataLabel}>
              End Date<sup>*</sup>
            </div>
            <DatePicker
              value={toDate}
              onChange={setToDate}
              placeholder={dateFormat}
              dateFormat={dateFormat}
            />
          </div>
        </>
      )}

      {!hasReportsAccess ? (
        <UpgradeTooltip displayTitle="Upgrade plan to generate reports!">
          <LockButton text={title} />
        </UpgradeTooltip>
      ) : (
        <Button onClick={handleClick} color="primary" fullWidth={!isDesktop}>
          <GetAppIcon className={css.mr8} />
          {title}
        </Button>
      )}
    </div>
  );
};

export default withComponentLibraryTheme(GenerateReport);
